// The engineer's lone worker safety check-in (Session AOB).
//
// One panel at the top of the job sheet with one control: Check in when you arrive, Check out when
// you leave, and Check in again while you are still there out of hours. Before this, an engineer
// alone in a motor room or a lift shaft had nothing on the platform that would notice if something
// went wrong and they stopped responding.
//
// AN EXPLICIT, ACTIVE TAP, by Director decision, and this component is the only thing on the phone
// that makes one. Saving the sheet, taking a photo and ticking the RAMS do NOT count and are not
// wired to it: a safety mechanism a person can satisfy without meaning to reports on the phone
// rather than on the person. (Submitting the completed sheet ENDS the session server-side, which is
// a different act: it records no check-in and moves no clock.)
//
// ONLINE ONLY, AND SAID SO OUT LOUD. This is the one place in the PWA where refusing to queue is a
// safety property rather than a limitation. A check-in says "I am alive at this moment"; storing one
// on the handset and delivering it when signal returns would let a tap made before somebody went
// into a basement silence an alert about every minute since. So nothing here touches the offline
// outbox, and a failed tap says plainly that the office still has them down as overdue.
//
// THE STATE IS ALWAYS READ LIVE for the same reason. A cached "you are checked in" is the single
// answer that must never be shown, so with no connection the panel says what it does not know
// instead of guessing.
import { useCallback, useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";
import type { LoneWorkerOwnSession } from "../../lib/api";

const OFFICE_PHONE = "0203 576 4760";

/** A stored instant as the London wall-clock time an engineer would read off a phone. */
export function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
}

/**
 * The line under a live check-in. Pure, so both states are testable without a render.
 *
 * The next-due time is stated even when it is hours away, because the honest thing to tell somebody
 * who has just checked in at 10am is that nothing more is owed until the evening. Hiding it would
 * leave an engineer wondering whether a check-in is about to be expected of them all afternoon.
 */
export function checkedInSummary(session: LoneWorkerOwnSession): string {
  const due = clockTime(session.nextCheckInDueAt);
  const at = clockTime(session.lastCheckInAt);
  return `Checked in at ${at}. Next check-in by ${due}, or check out when you leave.`;
}

interface Props {
  jobId: string;
  /** Called after a successful check-in or check-out, so the parent can refresh anything that reads it. */
  onChange?: () => void;
}

export function LoneWorkerCheckIn({ jobId, onChange }: Props) {
  const [session, setSession] = useState<LoneWorkerOwnSession | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "offline">("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.jobs.checkInState(jobId);
      setSession(res.session);
      setState("ready");
    } catch {
      // Never guess. An unknown state is reported as unknown, and the controls stay available so an
      // engineer who has just walked back into signal can simply tap.
      setState("offline");
    }
  }, [jobId]);

  useEffect(() => {
    setState("loading");
    setError(null);
    void load();
  }, [load]);

  async function checkIn() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.jobs.checkIn(jobId);
      setSession({
        sessionId: res.sessionId,
        startedAt: res.lastCheckInAt,
        lastCheckInAt: res.lastCheckInAt,
        checkInCount: res.checkInCount,
        nextCheckInDueAt: res.nextCheckInDueAt,
      });
      setState("ready");
      onChange?.();
      // DELIBERATELY NO RE-READ. The POST response already carries everything this panel shows, and a
      // follow-up GET could only make the answer worse: a transient failure or a slow read landing
      // after the state was set would flip a successful check-in back to "not checked in" on the
      // screen while the office had it. Being wrong in that direction is the one thing this control
      // must never do. (startedAt is set to this tap rather than the session's first one, which is
      // correct for a new session and unused for an extension: nothing here displays it.)
    } catch {
      setError(
        `Not sent. The office still has you down as not checked in. Move to where you have a signal and tap again, or ring the office on ${OFFICE_PHONE}.`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function checkOut() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.jobs.checkOut(jobId);
      setSession(null);
      setState("ready");
      onChange?.();
    } catch {
      setError(
        `Not sent. The office still has you down as on site. Move to where you have a signal and tap again, or ring the office on ${OFFICE_PHONE}.`,
      );
    } finally {
      setBusy(false);
    }
  }

  const on = session !== null;

  return (
    <section className={`mb-5 rounded-ele border ${on ? "border-[#63C41F]" : "border-ele-field-border"} bg-ele-card p-4`}>
      <div className="flex items-start gap-2">
        {on ? (
          <ShieldCheck aria-hidden size={18} className="mt-0.5 shrink-0 text-[#63C41F]" />
        ) : (
          <ShieldAlert aria-hidden size={18} className="mt-0.5 shrink-0 text-ele-body" />
        )}
        <div className="min-w-0">
          <p className="font-heading text-base text-ele-strong">Safety check-in</p>
          {state === "loading" ? (
            <p className="mt-0.5 font-body text-[13px] text-ele-body">Checking...</p>
          ) : state === "offline" ? (
            <p className="mt-0.5 font-body text-[13px] text-ele-body">
              No connection, so this cannot show whether you are checked in. Tap when you have a signal.
            </p>
          ) : on ? (
            <p className="mt-0.5 font-body text-[13px] text-ele-strong">{checkedInSummary(session)}</p>
          ) : (
            <p className="mt-0.5 font-body text-[13px] text-ele-body">
              Check in when you arrive so the office knows where you are. Check out when you leave.
            </p>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-[13px] text-ele-danger">
          {error}
        </p>
      )}

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          disabled={busy}
          onClick={() => void checkIn()}
          className="min-h-11 flex-1 rounded-ele bg-ele-green px-4 font-heading text-sm tracking-wide text-ele-bg disabled:opacity-50"
        >
          {busy ? "Sending..." : on ? "Check in again" : "Check in"}
        </button>
        {on && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void checkOut()}
            className="min-h-11 flex-1 rounded-ele border border-ele-field-border px-4 font-heading text-sm tracking-wide text-ele-body disabled:opacity-50"
          >
            Check out
          </button>
        )}
      </div>
    </section>
  );
}
